import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Shield,
    TrendingUp,
    Users,
    Award,
    ArrowUpRight,
    Heart,
    FileCheck,
    Target,
    CheckCircle,
    Star,
    Sparkles,
    Car,
    Plane,
    Home,
    AlertTriangle,
    Activity,
    Briefcase,
    PieChart,
    BarChart3,
    Layers,
    Clock,
    Wallet,
    UserCheck,
    GraduationCap,
    Calculator,
    Landmark,
    MessageSquare,
    Search,
    Crown,
} from "lucide-react";
import AnimatedSection, {
    StaggerContainer,
    StaggerItem,
    FloatingElement,
} from "../../components/ui/AnimatedSection";

// Service Categories with comprehensive offerings
import NewsBentoGrid from "../../components/home/NewsBentoGrid";
const serviceCategories = [
    {
        category: "Insurance Solutions",
        categoryIcon: Shield,
        description: "Protect what matters most with comprehensive coverage for health, life, motor, travel, and business needs.",
        services: [
            {
                icon: Heart,
                title: "Health Insurance",
                description: "Comprehensive medical coverage with wide hospital networks and seamless cashless claims.",
            },
            {
                icon: Shield,
                title: "Life Insurance",
                description: "Long-term financial security ensuring protection and stability for your loved ones.",
            },
            {
                icon: FileCheck,
                title: "Term Insurance",
                description: "High-value life coverage at affordable premiums for complete family protection.",
            },
            {
                icon: Car,
                title: "Motor Insurance",
                description: "Reliable coverage for cars and two-wheelers with fast claims and extensive network garages.",
            },
            {
                icon: Plane,
                title: "Travel Insurance",
                description: "Global travel protection covering medical emergencies, delays, and unforeseen risks.",
            },
            {
                icon: Home,
                title: "Home Insurance",
                description: "Complete protection for your home and belongings against natural and man-made risks.",
            },
            {
                icon: AlertTriangle,
                title: "Personal Accident Insurance",
                description: "Financial support against accidental injuries, disability, and loss of income.",
            },
            {
                icon: Activity,
                title: "Critical Illness Insurance",
                description: "Lump-sum coverage for major illnesses to reduce financial stress during treatment.",
            },
            {
                icon: Briefcase,
                title: "Commercial / Business Insurance",
                description: "Customized risk protection solutions for businesses, assets, and operations.",
            },
        ],
    },
    {
        category: "Investment & Wealth Solutions",
        categoryIcon: TrendingUp,
        description: "Build lasting wealth through strategic investments in mutual funds, equities, ETFs, and managed portfolios.",
        services: [
            {
                icon: PieChart,
                title: "Mutual Fund Investments",
                description: "Goal-based investment solutions with diversified portfolios and professional fund management.",
            },
            {
                icon: BarChart3,
                title: "Equity (Stocks) Investment",
                description: "Direct stock investments focused on long-term growth and wealth creation.",
            },
            {
                icon: Layers,
                title: "Exchange Traded Funds (ETF)",
                description: "Low-cost, transparent investment options tracking market indices and sectors.",
            },
            {
                icon: Clock,
                title: "Systematic Investment Plans (SIP)",
                description: "Disciplined, periodic investments designed for steady long-term wealth building.",
            },
            {
                icon: Crown,
                title: "Portfolio Management Services (PMS)",
                description: "Professionally managed portfolios tailored to your risk appetite and financial goals.",
            },
        ],
    },
    {
        category: "Retirement & Future Planning",
        categoryIcon: Users,
        description: "Secure your golden years with strategic retirement solutions and child education planning.",
        services: [
            {
                icon: Target,
                title: "Retirement Planning",
                description: "Strategic financial planning to ensure a stable and comfortable post-retirement life.",
            },
            {
                icon: Wallet,
                title: "Pension & Annuity Plans",
                description: "Guaranteed income solutions for financial independence after retirement.",
            },
            {
                icon: GraduationCap,
                title: "Child Education Planning",
                description: "Structured savings and investment plans to secure your child's future education.",
            },
        ],
    },
    {
        category: "Savings & Tax Planning",
        categoryIcon: Calculator,
        description: "Maximize your savings with tax-efficient investment strategies and fixed income options.",
        services: [
            {
                icon: Calculator,
                title: "Tax-Saving Investments",
                description: "Smart investment options designed to reduce tax liability while growing wealth.",
            },
            {
                icon: Landmark,
                title: "Fixed Income Solutions",
                description: "Stable and predictable returns through secure, low-risk investment instruments.",
            },
        ],
    },
    {
        category: "Advisory & Support Services",
        categoryIcon: MessageSquare,
        description: "Expert guidance for financial planning, risk assessment, and comprehensive wealth management.",
        services: [
            {
                icon: UserCheck,
                title: "Financial Planning Advisory",
                description: "Personalized financial guidance aligned with your life goals and risk profile.",
            },
            {
                icon: Search,
                title: "Risk Assessment & Insurance Advisory",
                description: "Expert evaluation to identify risks and recommend the right protection strategies.",
            },
            {
                icon: Crown,
                title: "Wealth Management Services",
                description: "End-to-end wealth solutions focused on growth, protection, and preservation.",
            },
        ],
    },
];


const stats = [
    { value: "5000+", label: "Clients Served" },
    { value: "15+", label: "Years Excellence" },
    { value: "₹500Cr", label: "Assets Managed" },
    { value: "100%", label: "Satisfaction" },
];

const testimonials = [
    {
        name: "Rajesh Patel",
        role: "Business Owner",
        content:
            "Seva Consultancy helped us find the perfect health insurance plan for our family. Their guidance was invaluable.",
        initials: "RP",
    },
    {
        name: "Sneha Mehta",
        role: "IT Professional",
        content:
            "Their investment advice has been exceptional. My portfolio has grown significantly under their guidance.",
        initials: "SM",
    },
    {
        name: "Amit Kumar",
        role: "Engineer",
        content:
            "Professional, knowledgeable, and always available. They made financial planning simple for us.",
        initials: "AK",
    },
];

export default function HomePage() {
    return (
        <div className="overflow-hidden">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center bg-cream">
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-pattern" />
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#d4b86a]/5 to-transparent" />

                <div className="relative max-w-7xl mx-auto px-6 py-32 w-full z-10">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Hero Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{
                                duration: 0.8,
                                ease: [0.4, 0, 0.2, 1],
                            }}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="inline-flex items-center mb-6"
                            >
                                <span className="badge">
                                    <Sparkles className="w-3 h-3 mr-2" />
                                    Trusted Since 2008
                                </span>
                            </motion.div>

                            <motion.h1
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.3 }}
                                className="text-gray-900 mb-6"
                            >
                                A Vision Beyond
                                <span className="block mt-1 gradient-text">
                                    Envision
                                </span>
                            </motion.h1>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.4 }}
                                className="line-accent mb-8"
                            >
                                <p className="text-lg text-gray-600 leading-relaxed max-w-lg">
                                    Comprehensive insurance and investment planning tailored to your life, goals, and legacy. We help individuals and families build financial security through smart protection, strategic investments, and expert guidance.
                                </p>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 }}
                                className="flex flex-wrap gap-4"
                            >
                                <Link to="/services">
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="btn-primary inline-flex items-center space-x-2"
                                    >
                                        <span>Explore Services</span>
                                        <ArrowUpRight className="w-4 h-4" />
                                    </motion.button>
                                </Link>
                                <Link to="/contact">
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="btn-outline"
                                    >
                                        <span>Free Consultation</span>
                                    </motion.button>
                                </Link>
                            </motion.div>
                        </motion.div>

                        {/* Hero Stats Card */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="hidden lg:block"
                        >
                            <FloatingElement duration={8} distance={10}>
                                <div className="relative">
                                    {/* Main Card */}
                                    <div className="glass-card rounded-2xl p-8 card-corner-accent">
                                        <div className="grid grid-cols-2 gap-6">
                                            {stats.map((stat, index) => (
                                                <motion.div
                                                    key={stat.label}
                                                    initial={{
                                                        opacity: 0,
                                                        y: 20,
                                                    }}
                                                    animate={{
                                                        opacity: 1,
                                                        y: 0,
                                                    }}
                                                    transition={{
                                                        delay:
                                                            0.6 + index * 0.1,
                                                    }}
                                                    className="text-center p-4"
                                                >
                                                    <p className="text-3xl font-display font-semibold gradient-text mb-1">
                                                        {stat.value}
                                                    </p>
                                                    <p className="text-xs uppercase tracking-wider text-gray-500">
                                                        {stat.label}
                                                    </p>
                                                </motion.div>
                                            ))}
                                        </div>

                                        <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                                            <p className="text-xs uppercase tracking-widest text-[#c9a84c]">
                                                Excellence in Financial Advisory
                                            </p>
                                        </div>
                                    </div>

                                    {/* Floating Badge */}
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 1 }}
                                        className="absolute -right-4 top-1/2 -translate-y-1/2 bg-gradient-to-br from-[#c9a84c] to-[#d4b86a] text-white px-5 py-4 rounded-xl shadow-lg"
                                    >
                                        <Award className="w-6 h-6 mb-1" />
                                        <p className="font-display font-semibold text-lg">
                                            15+ Years
                                        </p>
                                        <p className="text-xs opacity-80">
                                            of Trust
                                        </p>
                                    </motion.div>
                                </div>
                            </FloatingElement>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* News Grid */}
            <NewsBentoGrid />

            {/* Services Section - Our Expertise */}
            <section
                className="section-padding bg-white"
                aria-labelledby="expertise-heading"
                id="services"
            >
                <div className="container-custom">
                    <AnimatedSection className="text-center mb-16">
                        <span className="badge mb-4">Our Expertise</span>
                        <h2 id="expertise-heading" className="text-gray-900 mb-4">
                            Your Complete{" "}
                            <span className="gradient-text">Financial Solutions Partner</span>
                        </h2>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            Comprehensive insurance, investment, and wealth management services
                            designed to protect your assets and grow your financial future.
                        </p>
                    </AnimatedSection>

                    {/* Service Category Cards */}
                    <StaggerContainer className="flex flex-wrap justify-center gap-8">
                        {serviceCategories.map((category) => (
                            <StaggerItem key={category.category} className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)]">
                                <Link to="/services" className="block h-full">
                                    <motion.article
                                        whileHover={{ y: -10 }}
                                        transition={{ duration: 0.3 }}
                                        className="bg-white rounded-2xl p-8 h-full group cursor-pointer border border-gray-100 hover:border-[#d4b86a]/40 hover:shadow-xl transition-all duration-300"
                                    >
                                        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#c9a84c]/10 to-[#d4b86a]/20 flex items-center justify-center mb-6 group-hover:from-[#c9a84c] group-hover:to-[#d4b86a] transition-all duration-300">
                                            <category.categoryIcon className="w-7 h-7 text-[#c9a84c] group-hover:text-white transition-colors duration-300" />
                                        </div>

                                        <h3 className="text-xl font-display text-gray-900 mb-3 group-hover:text-[#c9a84c] transition-colors">
                                            {category.category}
                                        </h3>

                                        <p className="text-gray-600 text-sm leading-relaxed mb-4">
                                            {category.description}
                                        </p>

                                        <div className="flex items-center text-[#c9a84c] text-sm font-medium">
                                            <span>{category.services.length} Solutions</span>
                                            <ArrowUpRight className="w-4 h-4 ml-1 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                                        </div>
                                    </motion.article>
                                </Link>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>

                    <AnimatedSection delay={0.3} className="text-center mt-14">
                        <Link to="/services" aria-label="View all financial services offered by Seva Consultancy">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn-primary inline-flex items-center space-x-2"
                            >
                                <span>Explore All Services</span>
                                <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                            </motion.button>
                        </Link>
                    </AnimatedSection>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="section-padding bg-offwhite">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <AnimatedSection direction="left">
                            <div className="relative">
                                <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl">
                                    <img
                                        src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?q=80&w=2031"
                                        alt="Financial Planning"
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="absolute -bottom-6 -right-6 bg-white rounded-xl p-5 shadow-lg border border-gray-100"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="w-12 h-12 rounded-lg bg-[#d4b86a]/10 flex items-center justify-center">
                                            <Target className="w-6 h-6 text-[#c9a84c]" />
                                        </div>
                                        <div>
                                            <p className="text-2xl font-display text-gray-900">
                                                15+
                                            </p>
                                            <p className="text-xs uppercase tracking-wider text-gray-500">
                                                Years Trust
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            </div>
                        </AnimatedSection>

                        <AnimatedSection direction="right">
                            <span className="badge mb-4">Why Seva</span>
                            <h2 className="text-gray-900 mb-4">
                                Comprehensive Financial Protection & <span className="gradient-text">Wealth Planning</span>
                            </h2>
                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                A unified approach combining trusted insurance solutions, strategic financial planning, and dedicated client support to secure your present and future.
                            </p>

                            <div className="grid sm:grid-cols-2 gap-5">
                                {[
                                    {
                                        title: "Personalized Protection & Planning",
                                        desc: "Customized insurance and investment strategies aligned with your goals, risk profile, and life stages.",
                                    },
                                    {
                                        title: "Lifetime Client Partnership",
                                        desc: "Relationship-driven advisory focused on trust, transparency, and long-term client retention.",
                                    },
                                    {
                                        title: "Retirement & Wealth Continuity",
                                        desc: "Thoughtful planning to ensure financial independence in retirement and sustained wealth for future generations.",
                                    },
                                    {
                                        title: "Tax-Efficient Growth",
                                        desc: "Smart structuring of insurance and investments to minimize tax impact while maximizing returns.",
                                    },
                                    {
                                        title: "Reliable Support & Claims Assistance",
                                        desc: "End-to-end service with proactive support, seamless claims handling, and quick resolution.",
                                    },
                                    {
                                        title: "Expert Advisory & Risk Management",
                                        desc: "Experienced professionals providing informed guidance to protect assets and manage financial risks effectively.",
                                    },
                                ].map((item, index) => (
                                    <motion.div
                                        key={item.title}
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{
                                            delay: 0.2 + index * 0.1,
                                        }}
                                        className="flex items-start space-x-4 group"
                                    >
                                        <div className="w-6 h-6 rounded-full bg-[#d4b86a]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                            <CheckCircle className="w-4 h-4 text-[#c9a84c]" />
                                        </div>
                                        <div>
                                            <h4 className="text-gray-900 font-medium mb-1">
                                                {item.title}
                                            </h4>
                                            <p className="text-gray-600 text-sm">
                                                {item.desc}
                                            </p>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            <Link to="/about" className="inline-block mt-8">
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    className="btn-primary inline-flex items-center space-x-2"
                                >
                                    <span>Our Approach</span>
                                    <ArrowUpRight className="w-4 h-4" />
                                </motion.button>
                            </Link>
                        </AnimatedSection>
                    </div>
                </div>
            </section>


            {/* Testimonials */}
            <section className="section-padding bg-offwhite">
                <div className="container-custom">
                    <AnimatedSection className="text-center mb-12">
                        <span className="badge mb-4">Testimonials</span>
                        <h2 className="text-gray-900 mb-2">
                            Trusted by{" "}
                            <span className="gradient-text">Thousands</span>
                        </h2>
                    </AnimatedSection>

                    <StaggerContainer className="grid md:grid-cols-3 gap-6">
                        {testimonials.map((testimonial) => (
                            <StaggerItem key={testimonial.name}>
                                <motion.div
                                    whileHover={{ y: -5 }}
                                    className="glass-card rounded-xl p-6 h-full relative"
                                >
                                    <span className="absolute top-4 right-4 text-5xl font-display text-[#d4b86a]/10 leading-none">
                                        "
                                    </span>

                                    <div className="flex text-[#d4b86a] mb-4">
                                        {[...Array(5)].map((_, i) => (
                                            <Star
                                                key={i}
                                                className="w-4 h-4 fill-current"
                                            />
                                        ))}
                                    </div>

                                    <p className="text-gray-600 mb-6 leading-relaxed text-sm">
                                        "{testimonial.content}"
                                    </p>

                                    <div className="flex items-center">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#c9a84c] to-[#d4b86a] flex items-center justify-center mr-3">
                                            <span className="text-white text-sm font-semibold">
                                                {testimonial.initials}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="text-gray-900 font-medium text-sm">
                                                {testimonial.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {testimonial.role}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden section-dark">
                <div className="absolute inset-0 bg-pattern opacity-5" />
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#d4b86a]/10 to-transparent" />

                <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
                    <AnimatedSection>
                        <h2 className="text-white mb-4">
                            Ready to Begin Your{" "}
                            <span className="gradient-text">Journey</span>?
                        </h2>
                        <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                            Schedule a free consultation with our senior
                            advisors and discover how we can help secure your
                            financial future.
                        </p>
                        <Link to="/contact">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn-primary inline-flex items-center space-x-2"
                            >
                                <span>Book Free Consultation</span>
                                <ArrowUpRight className="w-4 h-4" />
                            </motion.button>
                        </Link>
                    </AnimatedSection>
                </div>
            </section>
        </div>
    );
}
